import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX,
  FiSettings,
  FiDroplet,
  FiWind,
  FiThermometer,
  FiActivity,
  FiClock,
  FiAlertCircle,
  FiPlusCircle,
} from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import {
  createSettingProfile,
  updateSettingProfile,
  deleteSettingProfile,
  getSettingProfilesByClient,
} from "../api/settingProfileApi";
import { toast } from "react-toastify";
import ControllerSettingsForm from "../components/SettingsProfileForm";

const ManageSettingProfiles = () => {
  const { clientData } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [showConfigForm, setShowConfigForm] = useState(false);

  const {
    register,
    reset,
    formState: { errors },
    getValues,
  } = useForm();

  useEffect(() => {
    const loadProfiles = async () => {
      try {
        if (!clientData?.client_id) return;
        const result = await getSettingProfilesByClient(clientData.client_id);
        setProfiles(result);
      } catch (err) {
        toast.error(err.message || "Failed to load setting profiles");
      }
    };

    loadProfiles();
  }, [clientData]);

  const openModal = (profile = null) => {
    setEditingProfile(profile);
    setShowModal(true);
    setShowConfigForm(false);
    if (profile) {
      reset({ name: profile.name });
    } else {
      reset({ name: "" });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProfile(null);
    reset();
    setShowConfigForm(false);
  };

  const handleConfigSubmit = async (config) => {
    try {
      const payload = {
        name:  getValues("name"),
        client_id: clientData.client_id,
        config,
      };

      let result;
      if (editingProfile) {
        result = await updateSettingProfile(editingProfile.profile_id, payload);
        setProfiles((prev) =>
          prev.map((p) => (p.profile_id === result.profile_id ? result : p))
        );
        toast.success("Profile updated successfully");
      } else {
        result = await createSettingProfile(payload);
        setProfiles((prev) => [...prev, result]);
        toast.success("Profile created successfully");
      }

      closeModal();
    } catch (err) {
      toast.error(err.message || "Failed to save profile");
    }
  };

  const deleteProfile = async (profileId) => {
    if (window.confirm("Are you sure you want to delete this profile?")) {
      try {
        const result = await deleteSettingProfile(profileId);
        setProfiles((prev) => prev.filter((p) => p.profile_id !== profileId));
        toast.success(result.message || "Profile deleted");
      } catch (err) {
        toast.error("Delete failed");
      }
    }
  };



  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-emerald-700">Device automation</p>
          <h1 className="text-2xl font-bold text-gray-900">Setting Profiles</h1>
          <p className="mt-1 text-sm text-gray-600">Review the thresholds and cycles assigned to your MCU configurations.</p>
        </div>
        <button
          onClick={() => openModal()}
          className="inline-flex items-center justify-center rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700"
        >
          <FiPlus className="mr-2 h-4 w-4" />
          Add Profile
        </button>
      </div>

      {profiles.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white px-5 py-14 text-center">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
            <FiSettings className="h-6 w-6" />
          </span>
          <h2 className="mt-4 text-base font-semibold text-gray-900">No setting profiles yet</h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-gray-500">Create a profile to define irrigation, climate, and humidity targets for your devices.</p>
          <button
            onClick={() => openModal()}
            className="mt-5 inline-flex items-center rounded-lg border border-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
          >
            <FiPlus className="mr-2 h-4 w-4" /> Create your first profile
          </button>
        </div>
      ) : (
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {profiles.map((profile) => (
          <div
            key={profile.profile_id}
            className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                  <FiSettings className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="truncate font-semibold text-gray-900">{profile.name}</h3>
                  <p className="text-xs text-gray-500">Configuration profile</p>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  onClick={() => openModal(profile)}
                  aria-label={`Edit ${profile.name}`}
                  title="Edit profile"
                  className="rounded-lg p-2 text-gray-500 transition hover:bg-emerald-50 hover:text-emerald-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-emerald-700"
                >
                  <FiEdit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => deleteProfile(profile.profile_id)}
                  aria-label={`Delete ${profile.name}`}
                  title="Delete profile"
                  className="rounded-lg p-2 text-gray-500 transition hover:bg-red-50 hover:text-red-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-600"
                >
                  <FiTrash2 className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-px bg-gray-100 sm:grid-cols-2">
              <section className="bg-white p-4">
                <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <FiDroplet className="h-4 w-4 text-sky-700" /> Irrigation
                </h4>
                <dl className="space-y-2.5">
                  <div className="flex items-center justify-between gap-3"><dt className="flex items-center gap-2 text-xs text-gray-500"><FiClock className="h-3.5 w-3.5" />Cycle</dt><dd className="text-right text-sm font-medium text-gray-800">On {profile.config?.irrigation?.on_cycle_hours ?? "—"} h</dd></div>
                  <div className="flex items-center justify-between gap-3"><dt className="text-xs text-gray-500">Water volume</dt><dd className="text-sm font-medium text-gray-800">{profile.config?.irrigation?.off_cycle_liters ?? "—"} L</dd></div>
                  <div className="flex items-center justify-between gap-3"><dt className="text-xs text-gray-500">Soil moisture</dt><dd className="text-sm font-medium text-gray-800">{profile.config?.irrigation?.soil_moisture_on ?? "—"}% / {profile.config?.irrigation?.soil_moisture_off ?? "—"}%</dd></div>
                  <div className="flex items-center justify-between gap-3"><dt className="flex items-center gap-2 text-xs text-gray-500"><FiAlertCircle className="h-3.5 w-3.5" />Alert range</dt><dd className="text-sm font-medium text-gray-800">{profile.config?.irrigation?.moisture_min_alert ?? "—"}% – {profile.config?.irrigation?.moisture_max_alert ?? "—"}%</dd></div>
                </dl>
              </section>

              <section className="bg-white p-4">
                <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <FiWind className="h-4 w-4 text-teal-700" /> Ventilation
                </h4>
                <dl className="space-y-2.5">
                  <div className="flex items-center justify-between gap-3"><dt className="flex items-center gap-2 text-xs text-gray-500"><FiActivity className="h-3.5 w-3.5" />CO₂ target</dt><dd className="text-sm font-medium text-gray-800">{profile.config?.ventilation?.co2_threshold ?? "—"} ppm</dd></div>
                  <div className="flex items-center justify-between gap-3"><dt className="flex items-center gap-2 text-xs text-gray-500"><FiAlertCircle className="h-3.5 w-3.5" />Alert range</dt><dd className="text-sm font-medium text-gray-800">{profile.config?.ventilation?.co2_min_alert ?? "—"} – {profile.config?.ventilation?.co2_max_alert ?? "—"} ppm</dd></div>
                </dl>
              </section>

              <section className="bg-white p-4">
                <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <FiThermometer className="h-4 w-4 text-orange-700" /> Air conditioning
                </h4>
                <dl className="space-y-2.5">
                  <div className="flex items-center justify-between gap-3"><dt className="text-xs text-gray-500">Temperature source</dt><dd className="text-sm font-medium capitalize text-gray-800">{(profile.config?.ac?.based_on ?? "—").replaceAll("_", " ")}</dd></div>
                  <div className="flex items-center justify-between gap-3"><dt className="flex items-center gap-2 text-xs text-gray-500"><FiPlusCircle className="h-3.5 w-3.5" />Target temperature</dt><dd className="text-sm font-medium text-gray-800">{profile.config?.ac?.target_temp ?? "—"}°C</dd></div>
                  <div className="flex items-center justify-between gap-3"><dt className="flex items-center gap-2 text-xs text-gray-500"><FiAlertCircle className="h-3.5 w-3.5" />Alert range</dt><dd className="text-sm font-medium text-gray-800">{profile.config?.ac?.temp_min_alert ?? "—"}°C – {profile.config?.ac?.temp_max_alert ?? "—"}°C</dd></div>
                </dl>
              </section>

              <section className="bg-white p-4">
                <h4 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <FiActivity className="h-4 w-4 text-indigo-700" /> Humidifier
                </h4>
                <dl className="space-y-2.5">
                  <div className="flex items-center justify-between gap-3"><dt className="text-xs text-gray-500">Turns on below</dt><dd className="text-sm font-medium text-gray-800">{profile.config?.humidifier?.humidity_on_below ?? "—"}%</dd></div>
                  <div className="flex items-center justify-between gap-3"><dt className="text-xs text-gray-500">Turns off above</dt><dd className="text-sm font-medium text-gray-800">{profile.config?.humidifier?.humidity_off_above ?? "—"}%</dd></div>
                  <div className="flex items-center justify-between gap-3"><dt className="flex items-center gap-2 text-xs text-gray-500"><FiAlertCircle className="h-3.5 w-3.5" />Alert range</dt><dd className="text-sm font-medium text-gray-800">{profile.config?.humidifier?.humidity_min_alert ?? "—"}% – {profile.config?.humidifier?.humidity_max_alert ?? "—"}%</dd></div>
                </dl>
              </section>
            </div>
          </div>
        ))}
      </div>
      )}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex justify-center items-center p-4 overflow-y-auto">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto">
            <button onClick={closeModal} className="absolute top-4 right-4 text-gray-500 hover:text-red-600">
              <FiX />
            </button>
            <h2 className="text-lg font-bold mb-4">
              {editingProfile ? "Edit Profile" : "Add New Profile"}
            </h2>

            {!showConfigForm ? (
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  setShowConfigForm(true);
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium">Profile Name</label>
                  <input
                    {...register("name", { required: "Name is required" })}
                    className="w-full px-3 py-2 border rounded-lg"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>}
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button type="button" onClick={closeModal} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
                    Cancel
                  </button>
                  <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    Next: Define Settings
                  </button>
                </div>
              </form>
            ) : (
              <ControllerSettingsForm
                initialConfig={editingProfile?.config || {}}
                onCancel={closeModal}
                onSubmit={handleConfigSubmit}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSettingProfiles;
